import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Typography, Stepper, Step, StepLabel, StepConnector, withStyles } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

// Hardcoded milestones for now - added "processing" as the first milestone
const milestones = {
  "received": {
    "order": 1,
    "label": "Recibida"
  },
  "processing": {
    "order": 2,
    "label": "Procesada"
  },
  "verifying": {
    "order": 3,
    "label": "Verif. bancaria"
  },
  "approved": {
    "order": 4,
    "label": "Transf. aprobada"
  },
  "onroute": {
    "order": 5,
    "label": "Transf. en curso"
  }
};

// Changed from green to golden color
const timelineGolden = 'rgb(204, 170, 102)';

// Custom connector line for the stepper - changed color to golden
const TimelineConnector = withStyles(theme => ({
  alternativeLabel: {
    top: 22,
  },
  active: {
    '& $line': {
      backgroundImage: `linear-gradient(90deg, ${timelineGolden} 0%, ${timelineGolden} 100%)`,
    },
  },
  completed: {
    '& $line': {
      backgroundImage: `linear-gradient(90deg, ${timelineGolden} 0%, ${timelineGolden} 100%)`,
    },
  },
  line: {
    height: 3,
    border: 0,
    backgroundColor: '#eaeaf0',
    borderRadius: 1,
  },
}))(StepConnector);

// Custom step icon - changed color to golden
const TimelineStepIcon = (props) => {
  const { active, completed } = props;
  
  return (
    <div
      style={{
        color: active || completed ? timelineGolden : '#eaeaf0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {completed ? (
        <CheckCircleIcon />
      ) : (
        <RadioButtonUncheckedIcon />
      )}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: 'rgba(204, 170, 102, 0.05)', // Light golden background
    borderLeft: `4px solid ${timelineGolden}`, // Golden left border
  },
  stepper: {
    backgroundColor: 'transparent',
    padding: theme.spacing(2, 0),
  },
  stepLabel: {
    '& .MuiStepLabel-label': {
      marginTop: theme.spacing(0.5),
      fontSize: '0.875rem',
      [theme.breakpoints.up('md')]: {
        fontSize: '1rem',
      },
    },
    '& .MuiStepLabel-active': {
      color: timelineGolden, // Golden for active step label
      fontWeight: 'bold',
    },
    '& .MuiStepLabel-completed': {
      color: timelineGolden, // Golden for completed step label
    },
  },
  title: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
}));

const PaymentTimeline = ({ requestedPayments = [] }) => {
  const classes = useStyles();
  
  // If no requested payments, don't show the timeline
  if (requestedPayments.length === 0) {
    return null;
  }
  
  // Get current date
  const currentDate = new Date();
  const currentDay = currentDate.getDate();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  // Find the oldest requested payment to use its timestamp
  // If timestamp is not available, use a default (beginning of month)
  let oldestRequestTimestamp;
  
  if (requestedPayments.length > 0) {
    // Sort payments by timestamp if available
    const sortedPayments = [...requestedPayments].sort((a, b) => {
      const timestampA = a.requestedTimestamp || 0;
      const timestampB = b.requestedTimestamp || 0;
      return timestampA - timestampB;
    });
    
    // Get the oldest request timestamp
    oldestRequestTimestamp = sortedPayments[0].requestedTimestamp;
  }
  
  // If no timestamp available, use day 1 of current month
  if (!oldestRequestTimestamp) {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    oldestRequestTimestamp = firstDayOfMonth.getTime();
  }
  
  // Convert timestamp to date object
  const requestDate = new Date(oldestRequestTimestamp);
  const requestDay = requestDate.getDate();
  const requestMonth = requestDate.getMonth();
  const requestYear = requestDate.getFullYear();
  
  // Check if the request is from a previous month/year
  const isFromPreviousMonth = (requestYear < currentYear) || 
                             (requestYear === currentYear && requestMonth < currentMonth);
  
  // Get total number of milestones
  const totalMilestones = Object.keys(milestones).length;
  
  // Calculate active step based on current date
  let activeStep = 0;
  
  // If request is from previous month, show all milestones completed
  if (isFromPreviousMonth) {
    activeStep = totalMilestones;
  } else if (currentDay <= 5) {
    // Days 1-5: Show only the first milestone (processing)
    activeStep = 1;
  } else {
    // After day 5: Space remaining milestones between request date and day 28
    
    // We've already passed the first milestone (processing)
    const remainingMilestones = totalMilestones - 1;
    
    // Calculate the day range between request day (or day 6 if request was earlier) and day 28
    const startDay = Math.max(requestDay, 6);
    const endDay = 28;
    const totalDays = endDay - startDay;
    
    // Calculate days per milestone
    const daysPerMilestone = totalDays / remainingMilestones;
    
    // If we're past day 28, all milestones are complete
    if (currentDay >= 28) {
      activeStep = totalMilestones;
    } else if (currentDay <= startDay) {
      // If we're at or before the start day, only the first milestone is active
      activeStep = 1;
    } else {
      // Calculate which milestone we're at
      const daysSinceStart = currentDay - startDay;
      const milestonesCompleted = Math.floor(daysSinceStart / daysPerMilestone);
      activeStep = 1 + Math.min(milestonesCompleted + 1, remainingMilestones);
    }
  }
  
  // Sort milestones by order
  const sortedMilestones = Object.values(milestones).sort((a, b) => a.order - b.order);

  return (
    <Paper className={classes.root} elevation={0}>
      <Typography variant="h6" className={classes.title}>
        Estado de tu solicitud de pago
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Tienes {requestedPayments.length} {requestedPayments.length === 1 ? 'pago' : 'pagos'} en proceso
      </Typography>
      
      <Stepper 
        alternativeLabel 
        activeStep={activeStep} 
        connector={<TimelineConnector />}
        className={classes.stepper}
      >
        {sortedMilestones.map((milestone, index) => (
          <Step key={milestone.order}>
            <StepLabel 
              StepIconComponent={TimelineStepIcon}
              className={classes.stepLabel}
            >
              {milestone.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Paper>
  );
};

export default PaymentTimeline;
